import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  FileTextOutlined,
  FlagFilled,
  IssuesCloseOutlined,
} from '@ant-design/icons'
import { OptimizingIcon } from '@components/_SiteWide/OrderDisplay/OptimizingIcon'
import { OrderStores } from '@components/_SiteWide/OrderDisplay/OrderStores'
import { OrderTerminals } from '@components/_SiteWide/OrderDisplay/OrderTerminals'
import {
  ReadableDifference,
  ShowRunOutWarningClass,
  convertTimeSpanToReadable,
  getDisplayTimezoneString,
} from '@utils/general'
import { Col, Popconfirm, Row, Tooltip } from 'antd'
import moment from 'moment'

import {
  ContractColumn,
  CreatedByColumn,
  CustomerColumn,
  DeliveryWindowColumn,
  DestinationCityColumn,
  DestinationNameColumn,
  DestinationPostalCodeColumn,
  DestinationStateColumn,
  DestinationStreetColumn,
  DriverNotesColumn,
  LoadingWindowColumn,
  LoadNumberColumn,
  MarketColumn,
  OriginCityColumn,
  OriginNameColumn,
  OriginPostalCodeColumn,
  OriginStateColumn,
  OriginStreetColumn,
  PriceTypeColumn,
  PurchaseOrderColumn,
  SectorColumn,
  SuppliersColumn,
  TotalVolumeColumn,
  destinationsComparator,
} from '@/pages/OrderManagement/components/Grid/Columns/DefaultColumnDefs'
import { OrderColumn, OrderRequestType } from '@components/OrderDispatch/OrderGrid/OrderColumn'
import { GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import dayjs from 'dayjs'
import { OrderFlagIcon } from '../../_SiteWide/Flags/FlagIcon'
import { OrderProducts } from '../../_SiteWide/OrderDisplay/OrderProducts'
import { OrderWindowTag, formatTimeWindow } from '../../_SiteWide/OrderDisplay/OrderTimeFormats'
import { checkRunout, isOrderRequestV1 } from '../util'
import { OrderUpdatedTag } from './OrderUpdatedTag'

export function getWindowComparator(key) {
  return (_a, _b, nodeA, nodeB, _isInverted) => {
    const rowA = nodeA?.data
    const rowB = nodeB?.data
    const now = moment()

    const valueA =
      rowA?.type === 'backhaul' && moment(rowA?.[`${key}_end`]).isBefore(now) ? now : moment(rowA?.[`${key}_start`])

    const valueB =
      rowB?.type === 'backhaul' && moment(rowB?.[`${key}_end`]).isBefore(now) ? now : moment(rowB?.[`${key}_start`])

    if (valueA.isSame(valueB)) {
      const endA = moment(rowA?.[`${key}_end`])
      const endB = moment(rowB?.[`${key}_end`])
      return endA.isAfter(endB) ? 1 : -1
    }

    return valueA.isAfter(valueB) ? 1 : -1
  }
}

export const loadingWindowComparator = getWindowComparator('load_window')
export const dispatchWindowComparator = getWindowComparator('dispatch_window')

export function getExtraOrderAttributes(order, lockedOrders, scopes) {
  const lockedOrder = lockedOrders?.find((item) => item?.order_id === order.id)
  return {
    lockedOrder: lockedOrder ? lockedOrder : false,
    editable: !lockedOrder && scopes?.order_dispatch.edit,
    hasComplianceHold: order?.flags?.some((flag) => flag?.type === 'compliance_hold' || flag?.type === 'credit_hold'),
    recentlyEdited:
      (order.updated_on && moment(order.updated_on).isAfter(moment().subtract(1, 'minutes'))) ||
      (order.user_edit && order.user_edit.isAfter(moment().subtract(1, 'minutes'))),
  }
}

function AssignToCarrierButton({ params, lockedOrders, scopes, triggerThirdPartyDispatchModal }) {
  if (params?.data?.type === OrderRequestType.OrderRequestRequested) return null
  const order = params?.data
  const attrs = getExtraOrderAttributes(order, lockedOrders, scopes)

  if (!attrs.editable) return null

  return (
    <Tooltip title='Assign To Carrier' placement='left'>
      <GraviButton
        appearance='outlined'
        disabled={attrs.locked || attrs.hasComplianceHold}
        icon={<ExportOutlined />}
        color='purple'
        onClick={() => triggerThirdPartyDispatchModal(order?.id)}
      />
    </Tooltip>
  )
}

function OrderStatusColumn({ order, triggerModal, updateOrder, lockedOrder }) {
  if (order?.type === OrderRequestType.OrderRequestRequested) return null
  return (
    <Row data-cy='order-dispatch-row'>
      <Col span={8}>
        <Tooltip title='Order Details'>
          <GraviButton
            id={`order-modal-btn-${order.id}`}
            icon={<FileTextOutlined />}
            size='small'
            onClick={() => triggerModal(order.id)}
          />
        </Tooltip>
      </Col>
      {lockedOrder && (
        <Col className='ml-4'>
          <OptimizingIcon lockedOrder={lockedOrder} />
        </Col>
      )}
      {!lockedOrder && order?.type?.toLowerCase() !== 'backhaul' && (
        <Col className='vertical-flex-center pl-2 pt-1' span={16}>
          <OrderFlagIcon row={order} updateOrder={updateOrder} />
        </Col>
      )}
      {order.third_party_candidate && (
        <Col className='pr-3 ' flex={1}>
          <Tooltip placement='right' title='Third Party Dispatch Candidate'>
            <div className='third-party-candidate-icon-container mr-1 mt-1 ml-2'>
              <FlagFilled />
            </div>
          </Tooltip>
        </Col>
      )}
    </Row>
  )
}

export function getOrderDispatchColumnDefs({
  triggerOrderModal,
  triggerThirdPartyDispatchModal,
  setOrderHistoryStoreId,
  setAuditOrderId,
  auditOrderId,
  lockedOrders,
  scopes,
  updateOrder,
  isPoPopoverVisible,
  setIsPopoverVisible,
  isManualPO,
  metadata,
  multiTruckEnabled,
  autoTrailerSwapEnabled,
  setRequestedOrderId,
  setSelectedRequestedOrder,
  autoAcceptOrderRequest,
  cancelOrderRequest,
}) {
  return [
    {
      field: '',
      minWidth: 140,
      cellRenderer: (params) => {
        const order = params?.data
        const attrs = getExtraOrderAttributes(order, lockedOrders, scopes)
        return (
          <div className='flex items-center justify-between h-full' style={{ gap: '0.5rem' }}>
            <div className='flex-1'>
              <OrderStatusColumn
                lockedOrder={attrs.lockedOrder}
                order={order}
                triggerModal={triggerOrderModal}
                updateOrder={updateOrder}
              />
            </div>
          </div>
        )
      },
    },
    OrderColumn(),
    MarketColumn(),
    SectorColumn(),
    {
      field: 'drops',
      headerName: 'Destinations',
      autoHeight: true,
      filter: 'agMultiColumnFilter',
      comparator: destinationsComparator,
      filterParams: {
        filters: [
          {
            title: 'Filter by Dest.',
            filter: 'agSetColumnFilter',
            display: 'subMenu',
            filterParams: {
              field: 'drops.location_name',
              buttons: ['reset'],
              values: metadata?.stores,
              valueFormatter: (params) => params?.value,
              valueGetter: (params) => {
                if (isOrderRequestV1(params?.data)) return params?.data?.site_number
                return params?.data?.drops?.map((drop) => drop.location_name)
              },
            },
          },
          {
            title: 'Filter by City',
            filter: 'agSetColumnFilter',
            display: 'subMenu',
            filterParams: {
              buttons: ['reset'],
              field: 'drops.city',
              values: metadata?.cities,
              valueFormatter: (params) => params?.value,
              valueGetter: (params) => {
                if (params?.data?.type === OrderRequestType.OrderRequestRequested) return params.data.site_city
                return params?.data?.drops?.map((drop) => drop.city)
              },
            },
          },
          {
            title: 'Filter by State',
            filter: 'agSetColumnFilter',
            display: 'subMenu',
            filterParams: {
              buttons: ['reset'],
              field: 'drops.state',
              values: metadata?.states,
              valueFormatter: (params) => params?.value,
              valueGetter: (params) => {
                if (params?.data?.type === OrderRequestType.OrderRequestRequested) return params.data.site_state
                return params?.data?.drops?.map((drop) => drop.state)
              },
            },
          },
        ],
      },
      enableRowGroup: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      valueGetter: (params) => {
        if (isOrderRequestV1(params?.data)) return `${params.data.site_number} - ${params.data.site_name}`
        return params?.data?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`)
      },
      cellRenderer: (params) => <OrderStores order={params?.data} setOrderHistoryStoreId={setOrderHistoryStoreId} />,
    },
    DestinationNameColumn(),
    DestinationStreetColumn(),
    DestinationCityColumn(),
    DestinationStateColumn(),
    DestinationPostalCodeColumn(),
    {
      field: 'loads',
      headerName: 'Origins',
      autoHeight: true,
      valueGetter: (params) => params?.data?.loads?.map((load) => load.location_name),
      menuTabs: ['filterMenuTab', 'generalMenuTab'],

      cellRenderer: (params) => {
        return <OrderTerminals order={params?.data} showShortName={false} />
      },
    },
    OriginNameColumn(),
    OriginStreetColumn(),
    OriginCityColumn(),
    OriginStateColumn(),
    OriginPostalCodeColumn(),
    {
      field: 'ratios',
      headerName: 'Products',
      enableRowGroup: true,
      autoHeight: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      valueGetter: (params) => params?.data?.product_ratios?.map((product) => product.name),
      cellRenderer: (params) => <OrderProducts order={params?.data} />,
    },
    SuppliersColumn(false),
    TotalVolumeColumn(),
    PriceTypeColumn(),
    PurchaseOrderColumn({
      isPopoverVisible: isPoPopoverVisible,
      setIsPopoverVisible,
      scopes,
      manualPO: isManualPO,
    }),
    {
      field: 'trailer_config',
      headerName: 'Trailer',
      hide: !multiTruckEnabled && !autoTrailerSwapEnabled,
    },
    LoadingWindowColumn(),
    DeliveryWindowColumn(),
    {
      field: 'dispatch_window_end',
      headerName: 'Runout',
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressMiniFilter: true,
        values: ['12 Hours', '24 Hours', '3 Days', 'Next Week'],
        valueGetter: (params) => checkRunout(params),
      },
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        if (
          nodeA?.data?.type?.toLowerCase() === 'backhaul' &&
          (nodeB?.data?.type?.toLowerCase() === 'regular' || nodeB?.data?.type?.toLowerCase() === 'site delivery')
        ) {
          return 1
        }
        if (
          (nodeA?.data?.type?.toLowerCase() === 'regular' || nodeA?.data?.type?.toLowerCase() === 'site delivery') &&
          nodeB?.data?.type?.toLowerCase() === 'backhaul'
        ) {
          return -1
        }
        if (nodeA?.data?.dispatch_window_end === nodeB?.data?.dispatch_window_end) {
          return 0
        }
        if (nodeA?.data?.dispatch_window_end > nodeB?.data?.dispatch_window_end) {
          return 1
        }
        return -1
      },
      valueGetter: (params) => params?.data?.dispatch_window_end,
      cellRenderer: (params) => (
        <span className={ShowRunOutWarningClass(params?.data?.dispatch_window_end)}>
          {ReadableDifference(
            params?.data?.dispatch_window_end,
            undefined,
            true,
            params?.data?.type?.toLowerCase() === 'backhaul'
          )}
        </span>
      ),
    },
    {
      field: 'accept_by',
      headerName: 'Accept By',
      filter: 'agDateColumnFilter',
      valueGetter: (params) => params?.data?.accept_by && formatters.DATE_TIME(dayjs(params?.data?.accept_by)),
      minWidth: getDisplayTimezoneString() ? 155 : 134,
    },
    {
      field: 'updated_on',
      headerName: 'Updated',
      filter: 'agDateColumnFilter',
      valueGetter: (params) => params?.data?.updated_on && dayjs(params?.data?.updated_on),
      cellRenderer: (params) => (
        <OrderUpdatedTag setAuditOrderId={setAuditOrderId} order={params?.data} auditOrderId={auditOrderId} />
      ),
      minWidth: getDisplayTimezoneString() ? 155 : 134,
    },
    CreatedByColumn(),
    {
      field: 'created_date',
      headerName: 'Requested',
      filter: 'agDateColumnFilter',
      valueGetter: (params) => params?.data?.created_date && formatters.DATE_TIME(dayjs(params?.data?.created_date)),
    },
    {
      field: 'turn_time',
      headerName: 'Turn Time',
      valueGetter: (params) => params?.data?.turn_time,
      cellRenderer: (params) => {
        const order = params?.data
        return (
          <Vertical justifyContent='center'>
            {(order?.turn_time && convertTimeSpanToReadable(order?.turn_time)) || 'N/A'}
          </Vertical>
        )
      },
      filterValueGetter: (params) => {
        const timespan = params?.data?.turn_time
        if (!timespan) return null
        const a = timespan.split(':')
        return parseInt(a[0]) * 60 + parseInt(a[1])
      },
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterPlaceholder: 'Turn Time Minutes',
      },
    },
    ContractColumn(),
    DriverNotesColumn(),
    LoadNumberColumn(),
    CustomerColumn(),
    {
      field: 'actions',
      pinned: 'right',
      width: 140,
      cellRenderer: (params) => {
        return (
          <Horizontal style={{ overflow: 'default', gap: '0.5rem' }}>
            <AssignToCarrierButton
              params={params}
              scopes={scopes}
              lockedOrders={lockedOrders}
              triggerThirdPartyDispatchModal={triggerThirdPartyDispatchModal}
            />
            {params?.data?.type === OrderRequestType.OrderRequestRequested && (
              <>
                <Tooltip title='Accept (Auto Supply)'>
                  <GraviButton
                    loading={autoAcceptOrderRequest.isPending && autoAcceptOrderRequest.variables === params.data.id}
                    icon={<CheckCircleOutlined />}
                    onClick={async () => {
                      autoAcceptOrderRequest.mutateAsync(params.data.id)
                    }}
                    className='outline'
                  />
                </Tooltip>
                <Tooltip title='Accept (Select Supply)'>
                  <GraviButton
                    icon={<IssuesCloseOutlined />}
                    onClick={() => {
                      setRequestedOrderId(params.data.id)
                      setSelectedRequestedOrder(params.data)
                    }}
                    className='outline'
                  />
                </Tooltip>
                <Popconfirm
                  icon={<ExclamationCircleFilled style={{ color: 'var(--theme-warning)' }} />}
                  title={'Decline Order Request?'}
                  description='If you decline this order request it will no longer be fulfilled. Are you sure you want to decline?'
                  onConfirm={() => cancelOrderRequest.mutateAsync(params.data.id)}
                  placement={'topLeft'}
                  cancelText={'No'}
                  okText={'Yes'}
                >
                  <Tooltip title='Cancel'>
                    <GraviButton icon={<CloseCircleOutlined />} className='outline' style={{ width: '32px' }} />
                  </Tooltip>
                </Popconfirm>
              </>
            )}
          </Horizontal>
        )
      },
    },
  ]
}
